import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Bell,
  BellOff,
  ChevronLeft,
  ChevronRight,
  Layers,
  Mail,
  Pencil,
  Plus,
  Ship,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AlertPeopleCell } from '../../components/alerts/alert-people-cell';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { LoadingState } from '../../components/loading-state';
import { useSubscribeAlert, useUnsubscribeAlert } from '../../hooks/use-alerts-subscription';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import { type AlertRule, alertsApi, compSetsApi, marketDataApi } from '../../lib/api-client';
import { queryKeys, STALE_TIMES } from '../../lib/query-keys';

export const Route = createFileRoute('/$orgPid/price-alerts')({
  component: PriceAlertsPage,
});

const SCOPE_LABELS: Record<string, string> = {
  comp_set: 'Competitor Set',
  market_boat: 'Market Boat',
};

const RULE_TYPE_LABELS: Record<string, string> = {
  price_drop: 'Price Drop',
  price_increase: 'Price Increase',
  price_change: 'Price Change',
  new_boat_in_comp_set: 'New Boat',
};

function PriceAlertsPage() {
  const { organization, isLoading } = useRequiredOrganization();

  if (isLoading) return <LoadingState />;

  return <PriceAlertsContent orgPid={organization.pid} />;
}

function PriceAlertsContent({ orgPid }: { orgPid: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [scopeFilter, setScopeFilter] = useState('all');
  const [ruleTypeFilter, setRuleTypeFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AlertRule | null>(null);
  const [editTarget, setEditTarget] = useState<AlertRule | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.alerts.list(orgPid, page, scopeFilter, ruleTypeFilter),
    queryFn: () =>
      alertsApi.list({
        page,
        limit: 20,
        ...(scopeFilter !== 'all' && { scopeType: scopeFilter }),
        ...(ruleTypeFilter !== 'all' && { ruleType: ruleTypeFilter }),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (pid: string) => alertsApi.remove(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.byOrg(orgPid) });
      toast.success('Alert rule deleted');
    },
    onError: () => toast.error('Failed to delete alert rule'),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      pid,
      data,
    }: {
      pid: string;
      data: { notifyInApp?: boolean; notifyEmail?: boolean };
    }) => alertsApi.update(pid, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.byOrg(orgPid) });
    },
    onError: () => toast.error('Failed to update alert'),
  });

  const subscribeMutation = useSubscribeAlert(orgPid);
  const unsubscribeMutation = useUnsubscribeAlert(orgPid);

  const rules = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Price Alerts</h1>
          <p className="text-muted-foreground">Get notified when competitor prices change</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-1 size-4" />
              Create Alert
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Alert Rule</DialogTitle>
              <DialogDescription>
                Configure when you want to be notified about price changes.
              </DialogDescription>
            </DialogHeader>
            <AlertRuleForm
              orgPid={orgPid}
              onSuccess={() => {
                setCreateOpen(false);
                void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.byOrg(orgPid) });
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="size-5" />
            Alert Rules
          </CardTitle>
          <CardDescription>
            {meta ? `${meta.total} rule${meta.total !== 1 ? 's' : ''} total` : 'Loading...'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={scopeFilter}
              onValueChange={(v) => {
                setScopeFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="comp_set">Competitor Set</SelectItem>
                <SelectItem value="market_boat">Market Boat</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={ruleTypeFilter}
              onValueChange={(v) => {
                setRuleTypeFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rule types</SelectItem>
                <SelectItem value="price_drop">Price Drop</SelectItem>
                <SelectItem value="price_increase">Price Increase</SelectItem>
                <SelectItem value="price_change">Price Change</SelectItem>
                <SelectItem value="new_boat_in_comp_set">New Boat</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead>Rule Type</TableHead>
                  <TableHead>Threshold</TableHead>
                  <TableHead>Notifications</TableHead>
                  <TableHead>People</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Last Triggered</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      <div className="text-muted-foreground">
                        <Bell className="mx-auto mb-2 size-8 opacity-50" />
                        <p>No alert rules configured.</p>
                        <p className="text-xs">
                          Create an alert to get notified about price changes.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ScopeIcon scopeType={rule.scopeType} />
                          <div className="min-w-0">
                            <Badge variant="outline" className="text-xs">
                              {SCOPE_LABELS[rule.scopeType] ?? rule.scopeType}
                            </Badge>
                            {rule.compSetName || rule.marketBoatName ? (
                              <div
                                className="mt-0.5 truncate text-xs text-muted-foreground"
                                title={rule.compSetName ?? rule.marketBoatName ?? undefined}
                              >
                                {rule.compSetName ?? rule.marketBoatName}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <RuleTypeBadge ruleType={rule.ruleType} />
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {rule.thresholdPct ? `>${rule.thresholdPct}%` : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            aria-label={rule.notifyInApp ? 'Disable in-app' : 'Enable in-app'}
                            aria-pressed={rule.notifyInApp}
                            onClick={() =>
                              updateMutation.mutate({
                                pid: rule.id,
                                data: { notifyInApp: !rule.notifyInApp },
                              })
                            }
                            className="rounded p-0.5 hover:bg-muted"
                          >
                            {rule.notifyInApp ? (
                              <Bell className="size-4 text-amber-500" />
                            ) : (
                              <BellOff className="size-4 text-muted-foreground" />
                            )}
                          </button>
                          <button
                            type="button"
                            aria-label={rule.notifyEmail ? 'Disable email' : 'Enable email'}
                            aria-pressed={rule.notifyEmail}
                            onClick={() =>
                              updateMutation.mutate({
                                pid: rule.id,
                                data: { notifyEmail: !rule.notifyEmail },
                              })
                            }
                            className="rounded p-0.5 hover:bg-muted"
                          >
                            <Mail
                              className={
                                rule.notifyEmail
                                  ? 'size-4 text-blue-500'
                                  : 'size-4 text-muted-foreground opacity-30'
                              }
                            />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <AlertPeopleCell
                          createdBy={rule.createdBy}
                          subscribers={rule.subscribers}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={rule.isActive}
                          aria-label="Toggle alert active"
                          onCheckedChange={(checked) =>
                            updateMutation.mutate({
                              pid: rule.id,
                              data: checked
                                ? { notifyInApp: true }
                                : { notifyInApp: false, notifyEmail: false },
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {rule.lastTriggeredAt
                          ? new Date(rule.lastTriggeredAt).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {rule.isSubscribed ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => unsubscribeMutation.mutate(rule.id)}
                              disabled={unsubscribeMutation.isPending}
                              aria-label="Unsubscribe from this alert"
                            >
                              Unsubscribe
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => subscribeMutation.mutate(rule.id)}
                              disabled={subscribeMutation.isPending}
                              aria-label="Subscribe to this alert"
                            >
                              Subscribe
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="size-8 p-0"
                            onClick={() => setEditTarget(rule)}
                            aria-label="Edit alert rule"
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="size-8 p-0 text-destructive"
                            onClick={() => setDeleteTarget(rule)}
                            aria-label="Delete alert rule"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                Page {meta.page} of {meta.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="mr-1 size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="ml-1 size-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Alert Rule"
        description="Are you sure you want to delete this alert rule? You will stop receiving notifications."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Alert Rule</DialogTitle>
            <DialogDescription>Update this alert's target, rule, and channels.</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <AlertRuleForm
              orgPid={orgPid}
              mode="edit"
              rule={editTarget}
              onSuccess={() => {
                setEditTarget(null);
                void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.byOrg(orgPid) });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScopeIcon({ scopeType }: { scopeType: string }) {
  switch (scopeType) {
    case 'comp_set':
      return <Layers className="size-4 text-muted-foreground" />;
    case 'market_boat':
      return <Ship className="size-4 text-muted-foreground" />;
    default:
      return null;
  }
}

function RuleTypeBadge({ ruleType }: { ruleType: string }) {
  const classes: Record<string, string> = {
    price_drop: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    price_increase: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    price_change: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    new_boat_in_comp_set: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  };

  return (
    <Badge variant="secondary" className={`text-xs ${classes[ruleType] ?? ''}`}>
      {RULE_TYPE_LABELS[ruleType] ?? ruleType}
    </Badge>
  );
}

// ── Create Alert Form ──

function AlertRuleForm({
  orgPid,
  onSuccess,
  mode = 'create',
  rule,
}: {
  orgPid: string;
  onSuccess: () => void;
  mode?: 'create' | 'edit';
  rule?: AlertRule;
}) {
  const [scopeType, setScopeType] = useState(rule?.scopeType ?? 'comp_set');
  const [scopeTarget, setScopeTarget] = useState(
    rule ? (rule.compSetId ?? rule.marketBoatId ?? '') : ''
  );
  const [ruleType, setRuleType] = useState(rule?.ruleType ?? 'price_drop');
  const [thresholdPct, setThresholdPct] = useState(rule?.thresholdPct ?? '10');
  const [notifyInApp, setNotifyInApp] = useState(rule?.notifyInApp ?? true);
  const [notifyEmail, setNotifyEmail] = useState(rule?.notifyEmail ?? false);
  const [error, setError] = useState('');

  // Fetch scope targets based on type
  const { data: compSetsData } = useQuery({
    queryKey: queryKeys.compSets.listTagged(orgPid, 'select'),
    queryFn: () => compSetsApi.list({ limit: 100 }),
    staleTime: STALE_TIMES.MEDIUM,
    enabled: scopeType === 'comp_set',
  });

  const { data: marketBoatsData } = useQuery({
    queryKey: queryKeys.market.boatsList(orgPid, 'select'),
    queryFn: () => marketDataApi.tracked({ limit: 50 }),
    staleTime: STALE_TIMES.MEDIUM,
    enabled: scopeType === 'market_boat',
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        scopeType,
        ruleType,
        ...(scopeType === 'comp_set' && scopeTarget && { compSetPid: scopeTarget }),
        ...(scopeType === 'market_boat' && scopeTarget && { marketBoatPid: scopeTarget }),
        ...(ruleType !== 'new_boat_in_comp_set' && { thresholdPct: Number(thresholdPct) }),
        notifyInApp,
        notifyEmail,
      };
      return mode === 'edit' && rule
        ? alertsApi.update(rule.id, payload)
        : alertsApi.create(payload);
    },
    onSuccess: () => {
      toast.success(mode === 'edit' ? 'Alert rule updated' : 'Alert rule created');
      onSuccess();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save alert'),
  });

  const targets =
    scopeType === 'comp_set'
      ? (compSetsData?.data ?? []).map((cs) => ({ id: cs.id, name: cs.name }))
      : (marketBoatsData?.data ?? []).map((mb) => ({ id: mb.id, name: mb.name }));

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!scopeTarget) {
          setError('Please select a target');
          return;
        }
        saveMutation.mutate();
      }}
    >
      {error && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <Label>Scope Type</Label>
        <Select
          value={scopeType}
          onValueChange={(v) => {
            setScopeType(v);
            setScopeTarget('');
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="comp_set">Competitor Set</SelectItem>
            <SelectItem value="market_boat">Market Boat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Target</Label>
        <Select value={scopeTarget || undefined} onValueChange={setScopeTarget}>
          <SelectTrigger>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {targets.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Rule Type</Label>
        <Select value={ruleType} onValueChange={setRuleType}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="price_drop">Price Drop</SelectItem>
            <SelectItem value="price_increase">Price Increase</SelectItem>
            <SelectItem value="price_change">Any Price Change</SelectItem>
            <SelectItem value="new_boat_in_comp_set">New Boat in Competitor Set</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {ruleType !== 'new_boat_in_comp_set' && (
        <div className="space-y-2">
          <Label>Threshold (%)</Label>
          <Input
            type="number"
            value={thresholdPct}
            onChange={(e) => setThresholdPct(e.target.value)}
            placeholder="10"
          />
          <p className="text-muted-foreground text-xs">
            Alert when price changes by more than this percentage.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>In-app notification</Label>
          <Switch checked={notifyInApp} onCheckedChange={setNotifyInApp} />
        </div>
        <div className="flex items-center justify-between">
          <Label>Email notification</Label>
          <Switch checked={notifyEmail} onCheckedChange={setNotifyEmail} />
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={saveMutation.isPending}>
        {saveMutation.isPending ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create Alert'}
      </Button>
    </form>
  );
}
